import { FC, useEffect, useState } from "react";
import "./MeetingRoomListScreen.css";
import backIcon from "../../assets/images/back-icon.svg";
import locationIcon from "../../assets/images/calendar-location-icon.svg";
import { useNavigate } from "react-router-dom";
import { MEETING_ROOM_TYPE, PURCHASE_TYPE } from "utils/constants";
import ReserveAcknowledgementDialog from "components/elements/ReserveAcknowledgementDialog";
import { getWorkspaceSlotDuration, isPaidSlot } from "utils/utils";
import useWSMApi from "services/api/useWSMApi";
import { useMutation } from "@tanstack/react-query";
import SnackbarAlert from "components/elements/SnackbarAlert";
import BecomeMemberDialog from "components/elements/BecomeMemberDialog";
import ROUTE_PATHS from "utils/routePaths";
import { useStorage } from "services/StorageProvider";
import { STORAGE_KEY } from "utils/strings";

const MeetingRoomListScreen: FC = () => {
  const navigate = useNavigate();
  const { userProfileData } = useStorage();
  const [selectedSlotData, setSelectedSlotData] = useState<any>(null);
  const [selectedRoom, setSelectedRoom] = useState<string>("");
  const [showAcknowledgementDialog, setShowAcknowledgementDialog] =
    useState<boolean>(false);
  const [showBecomeMemberDialog, setShowBecomeMemberDialog] =
    useState<boolean>(false);
  const { fetchUserCredits, reserveWSMSlot } = useWSMApi();

  useEffect(() => {
    const storedData = localStorage.getItem(STORAGE_KEY.SELECTED_SLOT_DATA);
    if (storedData) {
      setSelectedSlotData(JSON.parse(storedData));
    }
  }, []);

  const checkUserMembershipMutation = useMutation({
    mutationFn: () => fetchUserCredits(),
    onSuccess: () => {
      setShowAcknowledgementDialog(true);
    },
    onError: (error) => {
      if (error.message.includes("Please subscribe")) {
        setShowBecomeMemberDialog(true);
      }
    },
  });

  const slotReservationMutation = useMutation({
    mutationFn: () =>
      reserveWSMSlot({ slot_id: selectedSlotData?.id, pod_type: selectedRoom }),
    onSuccess: () => {
      setShowAcknowledgementDialog(false);
      const paymentData = {
        title: `${selectedSlotData?.title}`,
        type: PURCHASE_TYPE.EVENT,
      };
      localStorage.setItem(
        STORAGE_KEY.PAYMENT_SUCCESS_DATA,
        JSON.stringify(paymentData),
      );
      navigate(ROUTE_PATHS.WSM_ALL_SET);
    },
  });

  const handleBackButtonClick = () => {
    navigate(-1);
  };

  const handleContinueClick = () => {
    if (isPaidSlot(selectedSlotData?.service_type)) {
      checkUserMembershipMutation.mutate();
    } else {
      slotReservationMutation.mutate();
    }
  };

  const handleReservationConfirmClick = () => {
    slotReservationMutation.mutate();
  };

  return (
    <div className="wms-mobile-container">
      {slotReservationMutation?.isError ? (
        <SnackbarAlert type={"error"}>
          {slotReservationMutation.error.message}
        </SnackbarAlert>
      ) : (
        ""
      )}
      <div className="meeting-room-screen-container">
        <div className="meeting-room-header-container">
          <img
            src={backIcon}
            alt="dropdown-logo"
            className="image"
            onClick={handleBackButtonClick}
          />
          <div className="title">Choose the meeting room</div>
        </div>

        <div className="meeting-room-main-container">
          <div className="meeting-room-list-container">
            {selectedSlotData?.pod_types?.map((type: string, index: number) => (
              <div key={index}>
                <div
                  className={`meeting-room-list-card ${
                    selectedRoom === type && "selected"
                  }`}
                  onClick={() => setSelectedRoom(type)}
                >
                  <img
                    className="meeting-room-card-image"
                    src={selectedSlotData?.slot_image}
                    alt="meeting-slot-image"
                  ></img>
                  <div className="meeting-room-card-text-container">
                    <div className="title">{`${selectedSlotData?.title} - ${
                      MEETING_ROOM_TYPE.find((room) => room.value === type)
                        ?.name
                    }`}</div>
                    <div className="location">
                      <img src={locationIcon} alt="location-logo" />
                      {selectedSlotData?.location}
                    </div>
                    <div className="duration">
                      {getWorkspaceSlotDuration(
                        selectedSlotData?.start_time,
                        selectedSlotData?.end_time,
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <button
          className="meeting-room-continue-btn"
          disabled={selectedRoom === ""}
          onClick={handleContinueClick}
        >
          Continue
        </button>
      </div>
      <ReserveAcknowledgementDialog
        open={showAcknowledgementDialog}
        onClose={() => setShowAcknowledgementDialog(false)}
        slotData={selectedSlotData}
        seaterPreference={selectedRoom}
        onClickLetsDo={handleReservationConfirmClick}
        isLoading={slotReservationMutation?.isPending}
      />
      <BecomeMemberDialog
        profileStatus={userProfileData?.credit?.status}
        open={showBecomeMemberDialog}
        onClose={() => setShowBecomeMemberDialog(false)}
      />
    </div>
  );
};

export default MeetingRoomListScreen;
